import { getAuthUserData } from "../../../../utils/auth/getAuthUserData.ts";
import { NotifiableEvent } from "@/types/types";
import { noticeError } from "@/utils/newrelic/requests";

type NotifyMeFn = (params: {
  email: string;
  pimProductId: string;
  skuId: string;
  notificationsApiUrl: string;
  notifiableEventType: NotifiableEvent;
}) => Promise<{ showNotifyError: boolean; confirmationEmailSent: boolean }>;

export const notifyMe: NotifyMeFn = async ({
  email,
  pimProductId,
  skuId,
  notificationsApiUrl,
  notifiableEventType,
}) => {
  const loggedUserData = await getAuthUserData();

  const requester = loggedUserData
    ? { loggedIn: true, customerId: loggedUserData.customerId }
    : { loggedIn: false };

  const payload = {
    requester,
    recipient: {
      type: "EMAIL",
      toAddress: email,
    },
    product: {
      productId: pimProductId,
      skuId: skuId,
      ...(notifiableEventType === "backInStock"
        ? {}
        : { newlyInStock: notifiableEventType === "newlyInStock" }),
    },
  };

  return await fetch(notificationsApiUrl, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(handleResponse)
    .catch((e) => handleError(e, skuId));
};

function handleResponse(response: Response) {
  if (!response.ok) {
    throw Error(response.statusText);
  }
  if (response.status === 201 || response.status === 208) {
    return { confirmationEmailSent: true, showNotifyError: false };
  }
  return { confirmationEmailSent: false, showNotifyError: false };
}

function handleError(error: Error, skuId: string) {
  noticeError(`Notify API error: with skuId ${skuId}, error ${error}`);
  return { confirmationEmailSent: false, showNotifyError: true };
}

export function getNotificationsApiUrl(
  type: NotifiableEvent,
  notificationsApiBaseUrl: string,
  notificationsApiKey: string,
) {
  const BACK_IN_STOCK = "backInStock";
  const NEWLY_IN_STOCK = "newlyInStock";
  const ON_PREORDER_LAUNCH = "onPreorderLaunch";
  const ON_RELEASE = "onRelease";

  const API_NOTIFICATION_PATH =
    "/notifications-stock/api/v1/notifications/subscriptions/";
  const API_BACK_IN_STOCK = "back-in-stock";
  const API_PRODUCT_LAUNCH = "product-launch";

  let apiEndpoint;
  switch (type) {
    case BACK_IN_STOCK:
      apiEndpoint = API_BACK_IN_STOCK;
      break;
    case NEWLY_IN_STOCK:
    case ON_RELEASE:
    case ON_PREORDER_LAUNCH:
      apiEndpoint = API_PRODUCT_LAUNCH;
      break;
    default:
      apiEndpoint = "";
  }

  return `${notificationsApiBaseUrl}${API_NOTIFICATION_PATH}${apiEndpoint}?key=${notificationsApiKey}`;
}
